import { JobRating } from './JobOrder';
import { MarinerProfile } from "./MarinerProfile";
import { User } from "./User";
import { Vessel } from "./Vessel";

export type Evaluation = {
  id?: number;
  permissions?: EvaluationPermissions;
  profile?: MarinerProfile;
  rating?: JobRating;
  vessel: Vessel | undefined;
  createdDate?: string;
  fromDate: string | undefined;
  toDate: string | undefined;
  lastUpdatedDate: string | undefined;
  scores: EvaluationScore[];
  assessor1UserId: string | undefined;
  assessor1?: User;
  assessor1Comment: string | undefined;
  assessor1Title?: string | undefined;
  assessor2UserId: string | undefined;
  assessor2?: User;
  assessor2Comment: string | undefined;
  assessor2Title?: string | undefined;
  finalReviewerUserId: string | undefined;
  finalReviewer?: User;
  finalReviewerComment: string | undefined;
  finalReviewerTitle?: string | undefined;
  evaluatee?: MarinerProfile;
  evaluateeComment: string | undefined;
  status?: EvaluationStatus;
  assessor1Signature?: EvaluationSignature;
  assessor2Signature?: EvaluationSignature;
  finalReviewerSignature?: EvaluationSignature;
  evaluateeSignature?: EvaluationSignature;
  assessor2RejectionNote?: string;
  finalReviewerRejectionNote?: string;
  archivedDate?: string;
  form?: EvaluationForm;
  readyForPromotion?: boolean | undefined;
  hasPreviousWarnings?: boolean | undefined;
  reviewedWithMariner?: boolean | undefined;

};

export type EvaluationForm = {
  id?: number;
  name: string;
  questions: EvaluationQuestion[];
  createdAt: string;
};

export type EvaluationQuestion = {
  id?: number;
  form: EvaluationForm | undefined;
  questionText: string;
  description: string;
  subSection: string;
  position: number;
};

export type EvaluationScore = {
  id?: number;
  evaluation?: Evaluation;
  question?: EvaluationQuestion;
  score: number | undefined;
  notApplicable: boolean | undefined;
};

// enum for Evaluation status field
export enum EvaluationStatus {
  EVALUATION_INITIATED = "EVALUATION_INITIATED",
  ASSESSOR_1_EDIT = "ASSESSOR_1_EDIT",
  ASSESSOR_1_SUBMIT = "ASSESSOR_1_SUBMIT",
  ASSESSOR_2_APPROVE = "ASSESSOR_2_APPROVE",
  ASSESSOR_2_RETURN = "ASSESSOR_2_RETURN",
  FINAL_REVIEWER_APPROVE = "FINAL_REVIEWER_APPROVE",
  FINAL_REVIEWER_RETURN = "FINAL_REVIEWER_RETURN",
  SENIOR_OFFICER_SIGN = "SENIOR_OFFICER_SIGN",
  EVALUATION_ARCHIVED = "EVALUATION_ARCHIVED",
}

// ui representation of Evaluation status field
export const EvaluationStatusValue = {
  [EvaluationStatus.EVALUATION_INITIATED as string]: "Evaluation Initiated",
  [EvaluationStatus.ASSESSOR_1_EDIT as string]: "Assessor 1 Editing",
  [EvaluationStatus.ASSESSOR_1_SUBMIT as string]: "Assessor 1 Submitted",
  [EvaluationStatus.ASSESSOR_2_APPROVE as string]: "Assessor 2 Approved",
  [EvaluationStatus.ASSESSOR_2_RETURN as string]: "Assessor 2 Returned",
  [EvaluationStatus.FINAL_REVIEWER_APPROVE as string]: "Final Review Approved",
  [EvaluationStatus.FINAL_REVIEWER_RETURN as string]: "Final Review Returned",
  [EvaluationStatus.SENIOR_OFFICER_SIGN as string]: "Signed By Evaluatee",
  [EvaluationStatus.EVALUATION_ARCHIVED as string]: "Archived",
};

export type EvaluationSignature = {
  id?: number;
  fileKey: string;
  signatureDate: string;
  imageBase64?: string;
  signatureText?: string;
};

export type EvaluationMetadata = {
  seniorNotStartedCount: number;
  seniorInProgressCount: number;
  seniorCompletedCount: number;
  juniorEvaluationCount: number;
  unlicensedEvaluationCount: number;
};

export type EvaluationInsightView = {
  vessel: Vessel;
  profile: MarinerProfile;
  rating?: JobRating;
  evaluationId: number;
  status: string;
  rejectionNote?: string;
  assessor1Email: string;
  assessor2Email: string;
  finalReviewerEmail: string;
  lastUpdatedDate: string | undefined;
  currentAssessorName: string | undefined;
  currentAssessorTitle: string | undefined;
  dispatchId?: number;
};

export type MarinerEvaluationInsightsView = {
  evalsNeedingAction: EvaluationInsightView[];
  activeEvaluations: EvaluationInsightView[];
  offboardingSoon: EvaluationInsightView[];
};

export type EvaluationInsightViews = {
  seniorOfficerEvaluations: EvaluationInsightView[];
  juniorOfficerEvaluations: EvaluationInsightView[];
  unlicensedEvaluations: EvaluationInsightView[];
};

export type EvaluationArchiveViews = {
  seniorOfficerEvaluations: Evaluation[];
  juniorOfficerEvaluations: Evaluation[];
  unlicensedEvaluations: Evaluation[];
};

export type EvaluationPermissions = {
  assessor1: boolean;
  assessor2: boolean;
  finalReviewer: boolean;
  evaluatee: boolean;
  canEdit: boolean;
  canComment: boolean;
  canSubmit: boolean;
  canApprove: boolean;
  canArchive: boolean;
  canSign: boolean;
  canRemind: boolean;
  canReassignAssessor1: boolean;
  canReassignAssessor2: boolean;
  canReassignAssessor3: boolean;
};

export type EvaluationAssessor = {
  userId: string;
  name: string;
  rating: string;
  mmcId: string | null;
  email: string;
}
